import React from "react";
import { useMsalAuthentication, useMsal } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";

import { iffLoginRequest } from "../services/authenticationService/msal";
import useLocalStorage from "./useLocalStorage";

import MSALUser from "../domains/MSALUser";

type AuthContext = {
  user: MSALUser | null
  setUser: React.Dispatch<React.SetStateAction<MSALUser | null>>
}

const AuthStateContext = React.createContext<AuthContext | undefined>(undefined);

type AuthProviderProps = {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useLocalStorage<MSALUser | null>("AuthContext", null);

  const { accounts } = useMsal();

  React.useEffect(() => {
    const [user] = accounts as Array<MSALUser>;
    if (user) {
      setUser({
        homeAccountId: user?.homeAccountId,
        name: user.name,
        username: user.username,
        apiAccessTokenExpiresOn: undefined,
      });
    }
  }, [accounts]);

  const loginHint = React.useMemo(
    () => ({ ...iffLoginRequest, loginHint: user?.username }),
    [user?.username, iffLoginRequest],
  );

  useMsalAuthentication(InteractionType.Silent, loginHint);

  return (
    <AuthStateContext.Provider value={{ user, setUser }}>
      {children}
    </AuthStateContext.Provider>
  );
}

export function useAuthContext() {
  const context = React.useContext(AuthStateContext);
  if (context === undefined)
    throw new Error("useAuthState must be used within a AuthProvider");

  return context;
}
